import { useState } from 'react';
import type { FormEvent } from 'react';

interface SubjectLockScreenProps {
  subjectName: string;
  onUnlock: (password: string) => boolean;
  onBack: () => void;
}

function SubjectLockScreenStyles() {
  return (
    <style>{`
      .subject-lock-root {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 28px;
        position: relative; z-index: 1;
        background:
          radial-gradient(circle at 50% -10%, rgba(194,162,78,0.1), transparent 50%);
      }
      .subject-lock-card {
        width: min(100%, 440px);
        border-radius: 4px 4px 16px 16px;
        border: 1px solid var(--gilt-line);
        border-top: 1px solid var(--gilt);
        background: var(--ink-800);
        box-shadow: var(--shadow-ink);
        padding: 30px 28px 26px;
        animation: lock-card-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      @keyframes lock-card-in {
        from { opacity: 0; transform: translateY(12px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .subject-lock-badge {
        width: 46px;
        height: 46px;
        border-radius: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: var(--gilt-dim);
        border: 1px solid var(--gilt-line);
        color: var(--gilt);
        margin-bottom: 16px;
      }
      .subject-lock-title {
        font-family: var(--font-display);
        font-optical-sizing: auto;
        font-size: clamp(1.6rem, 3.6vw, 2.1rem);
        color: var(--vellum);
        font-weight: 500;
        line-height: 1.12;
        margin-bottom: 8px;
      }
      .subject-lock-sub {
        font-family: var(--font-read);
        color: var(--vellum-dim);
        font-size: 0.98rem;
        line-height: 1.55;
        margin-bottom: 18px;
      }
      .subject-lock-sub strong {
        color: var(--gilt-bright);
        font-weight: 500;
        font-style: italic;
      }
      .subject-lock-input {
        width: 100%;
        background: var(--ink-850);
        border: 1px solid var(--gilt-line);
        color: var(--vellum);
        caret-color: var(--gilt);
        border-radius: 9px;
        font-family: var(--font-mono);
        font-size: 0.95rem;
        letter-spacing: 0.05em;
        padding: 12px 13px;
        outline: none;
        transition: border-color 0.18s ease;
      }
      .subject-lock-input:focus {
        border-color: var(--gilt);
      }
      .subject-lock-error {
        margin-top: 11px;
        font-family: var(--font-ui);
        font-size: 0.82rem;
        color: var(--rose);
      }
      .subject-lock-actions {
        margin-top: 18px;
        display: flex;
        gap: 10px;
      }
      .subject-lock-btn {
        height: 42px;
        border-radius: 9px;
        font-family: var(--font-ui);
        font-size: 0.86rem;
        font-weight: 600;
        border: 1px solid transparent;
        cursor: pointer;
        transition: transform 0.14s cubic-bezier(0.16,1,0.3,1), background 0.18s ease, opacity 0.16s ease, border-color 0.16s ease;
      }
      .subject-lock-btn:active { transform: scale(0.97); }
      .subject-lock-btn-secondary {
        flex: 1;
        color: var(--vellum);
        background: var(--ink-750);
        border-color: var(--gilt-line);
      }
      .subject-lock-btn-secondary:hover { background: var(--ink-700); }
      .subject-lock-btn-primary {
        flex: 1.2;
        color: var(--ink-900);
        background: var(--gilt);
      }
      .subject-lock-btn-primary:hover:not(:disabled) { background: var(--gilt-bright); }
      .subject-lock-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `}</style>
  );
}

export default function SubjectLockScreen({
  subjectName,
  onUnlock,
  onBack,
}: SubjectLockScreenProps) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!password.trim()) {
      setError('Introduce la contraseña de acceso.');
      return;
    }
    setError(null);
    const isValid = onUnlock(password);
    if (!isValid) {
      setError('La contraseña no es válida para esta asignatura.');
    }
  };

  return (
    <>
      <SubjectLockScreenStyles />
      <div className="subject-lock-root">
        <form className="subject-lock-card" onSubmit={handleSubmit}>
          <div className="subject-lock-badge">
            <i className="ri-lock-2-line text-2xl" />
          </div>
          <h2 className="subject-lock-title">Asignatura protegida</h2>
          <p className="subject-lock-sub">
            Introduce la contraseña para acceder a <strong>{subjectName}</strong>.
          </p>

          <input
            className="subject-lock-input"
            type="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(null);
            }}
            placeholder="Contraseña de asignatura"
            autoFocus
          />

          {error && <p className="subject-lock-error">{error}</p>}

          <div className="subject-lock-actions">
            <button type="button" className="subject-lock-btn subject-lock-btn-secondary" onClick={onBack}>
              Volver
            </button>
            <button type="submit" className="subject-lock-btn subject-lock-btn-primary" disabled={!password.trim()}>
              Desbloquear
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
